import {
  Button,
  closeCurrentDialog,
  DataStatus,
  Label,
  openDialog,
  Table,
  TableRow,
} from 'platform/components';
import {Box, HStack, Text} from 'platform/foundation';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';

import {useFieldArray} from 'react-hook-form';

import {isEmpty, isNil} from 'ramda';

import {useListWarrantyTypeQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, parseDate} from 'shared/web';

import {VehicleWarrantyRow} from '../../core/schema/VehicleWarrantyRowSchema';
import {useVehicleFormContext} from '../../kit/hooks/useVehicleFormContext';
import {VehicleForm as VFKit} from '../../kit/VehicleForm';
import {VehicleWarrantyRowForm} from '../components/VehicleWarrantyRowForm';

const TEST_ID = 'vehicle-warranties';

// Warranties are assigned per vehicle id, so on create they are collected here and posted
// once the vehicle exists (see useVehicleWarrantySync).
export function VehicleWarrantiesSubSection() {
  const {control} = useVehicleFormContext();
  const formatDateTime = useDateTimeFormatter();
  const formatNumber = useFormatNumber();

  const {data: warrantyTypes} = useListWarrantyTypeQuery();

  const {fields, append, update, remove} = useFieldArray({control, name: 'warranties'});

  const getProductIdsExcept = (excludedIndex: number | null) =>
    fields
      .filter((field, index) => index !== excludedIndex)
      .map((field) => field.warrantyProductId);

  const getWarrantyTypeLabel = (warrantyProductId: string) =>
    warrantyTypes?.find((warrantyType) => warrantyType.id === warrantyProductId)?.description ??
    EMPTY_PLACEHOLDER;

  const formatRowDate = (date: string | null) =>
    isNil(date) ? EMPTY_PLACEHOLDER : formatDateTime('dateShort', parseDate(date));

  const formatRowOdometer = (odometerKm: number | null) =>
    isNil(odometerKm)
      ? EMPTY_PLACEHOLDER
      : `${formatNumber(odometerKm)} ${i18n.t('general.metric.km')}`;

  const handleAssignWarranty = () =>
    openDialog(
      <VehicleWarrantyRowForm
        existingProductIds={getProductIdsExcept(null)}
        onSubmit={(row) => {
          append(row);
          closeCurrentDialog();
        }}
        onDiscard={closeCurrentDialog}
        data-testid={`${TEST_ID}-assignDialog`}
      />,
      {title: i18n.t('entity.vehicleWarranty.actions.assign')}
    );

  const handleEditWarranty = (index: number, row: VehicleWarrantyRow) =>
    openDialog(
      <VehicleWarrantyRowForm
        existingProductIds={getProductIdsExcept(index)}
        initialValue={row}
        onSubmit={(updatedRow) => {
          update(index, updatedRow);
          closeCurrentDialog();
        }}
        onDiscard={closeCurrentDialog}
        data-testid={`${TEST_ID}-editDialog`}
      />,
      {title: i18n.t('entity.vehicleWarranty.actions.edit')}
    );

  return (
    <VFKit.SubSection
      data-testid={TEST_ID}
      title={i18n.t('entity.vehicleWarranty.labels.warranties', {defaultValue: 'Warranties'})}
      useCard
    >
      <HStack justify="flex-end">
        <Button
          variant="link"
          leftIcon="content/add_circle"
          title={i18n.t('entity.vehicleWarranty.actions.assign')}
          onClick={handleAssignWarranty}
          data-testid={`${TEST_ID}-assign`}
        />
      </HStack>
      <DataStatus isEmpty={isEmpty(fields)} minHeight={20}>
        <Table
          columns={[
            {
              element: (
                <Box paddingHorizontal={2}>
                  <Label>{i18n.t('entity.warrantyType.labels.warrantyType')}</Label>
                </Box>
              ),
            },
            {
              element: (
                <Box paddingHorizontal={2}>
                  <Label>{i18n.t('entity.vehicle.labels.startDate')}</Label>
                </Box>
              ),
            },
            {
              element: (
                <Box paddingHorizontal={2}>
                  <Label>{i18n.t('entity.vehicle.labels.endDate')}</Label>
                </Box>
              ),
            },
            {
              element: (
                <Box paddingHorizontal={2}>
                  <Label>{i18n.t('entity.vehicleWarranty.labels.startOdometer')}</Label>
                </Box>
              ),
            },
            {
              element: (
                <Box paddingHorizontal={2}>
                  <Label>{i18n.t('entity.vehicleWarranty.labels.endOdometer')}</Label>
                </Box>
              ),
            },
            {element: <Box />},
          ]}
          data-testid={TEST_ID}
        >
          {fields.map((field, index) => (
            <TableRow
              key={field.id}
              actions={{
                primary: [
                  {
                    icon: 'image/edit',
                    title: i18n.t('general.actions.edit'),
                    onClick: () => handleEditWarranty(index, field),
                  },
                  {
                    icon: 'action/delete',
                    title: i18n.t('general.actions.delete'),
                    severity: 'danger',
                    onClick: () => remove(index),
                  },
                ],
              }}
              data-testid={`${TEST_ID}-[${index}]`}
            >
              <Box padding={2}>
                <Text size="small">{getWarrantyTypeLabel(field.warrantyProductId)}</Text>
              </Box>
              <Box padding={2}>
                <Text size="small">{formatRowDate(field.startDate)}</Text>
              </Box>
              <Box padding={2}>
                <Text size="small">{formatRowDate(field.endDate)}</Text>
              </Box>
              <Box padding={2}>
                <Text size="small">{formatRowOdometer(field.startOdometerKm)}</Text>
              </Box>
              <Box padding={2}>
                <Text size="small">{formatRowOdometer(field.endOdometerKm)}</Text>
              </Box>
            </TableRow>
          ))}
        </Table>
      </DataStatus>
    </VFKit.SubSection>
  );
}
